import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon
} from "@heroicons/react/24/outline";
import { getAuth } from "firebase/auth";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import {
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthStatus } from "../components/hooks/useAuthStatus";
import Spinner from "../components/layout/Spinner";
import AttackZones from "../components/matches/AttackZones";
import BoxScore from "../components/matches/BoxScore";
import CatapultReport from "../components/matches/CatapultReport";
import HittingChartReport from "../components/matches/HittingChartReport";
import MatchCentre from "../components/matches/MatchCentre";
import MatchSummary from "../components/matches/MatchSummary";
import PowerPlayReport from "../components/matches/PowerPlayReport";
import SideoutReport from "../components/matches/SideoutReport";
import Summary from "../components/matches/Summary";
import VSSReports from "../components/matches/VSSReports";
import { enrichMatchWithCatapultData } from "../components/utils/CatapultFocusFile";
import { getLiveRefreshInterval } from "../components/utils/Constants";
import { recordMatchView } from "../components/utils/dbutils";
import {
  calculateDVWStats,
  generateMatch,
  initWithDVWCompressedBuffer
} from "../components/utils/DVWFile";
import { fsFetchCatapultFocusData, usingFirestore } from "../components/utils/firestoreData";
import {
  calculatePSVBStats,
  initWithPSVBCompressedBuffer,
  parseLatestPSVBStats,
} from "../components/utils/PSVBFile";
import { calculateSideoutStats } from "../components/utils/StatsItem";
import {
  generateUUID,
  unzipBuffer
} from "../components/utils/Utils";
import { useLanguage } from "../context/Language/LanguageContext";
import {
  getLatestStats,
  getSession,
  storeSession,
} from "../context/VBLiveAPI/VBLiveAPIAction";
import VBLiveAPIContext from "../context/VBLiveAPI/VBLiveAPIContext";

function Session() {
  const { session, appName, loading, dispatch } = useContext(VBLiveAPIContext);
  const location = useLocation();
  const {
    sessionId,
    dvwFileData,
    psvbFileData,
    filename,
    msession,
    origin: fileOrigin,
    initialInDatabase,
    winScore,
    winByTwo,
  } = getSessionState(location?.state);
  const { currentUser } = useAuthStatus();
  const { t } = useLanguage();
  const params = useParams();
  const [match, setMatch] = useState(null);
  const [latest, setLatest] = useState(null);
  const [selectedGame, setSelectedGame] = useState(() => location.state?.vssReport?.selectedGame ?? 0);
  const [selectedTeam, setSelectedTeam] = useState(() => location.state?.vssReport?.selectedTeam ?? 0);
  const [currentReport, setCurrentReport] = useState(() => location.state?.vssReport ? 11 : 7);
  const [counter, setCounter] = useState(0);
  const [showingVideo, setShowingVideo] = useState(false);
  const [, forceUpdate] = useState(0);
  const navigate = useNavigate();
  const countRef = useRef(counter);
  const refreshInterval = getLiveRefreshInterval();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isLiveSession, setIsLiveSession] = useState(false);
  const [inDatabase, setInDatabase] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lastUtcLastUpdate, setLastUtcLastUpdate] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  const getLatest = useCallback(async () => {
    dispatch({ type: "SET_LOADING" });

    const sessionData = await getSession(sessionId);
    if (!sessionData) {
      return;
    }
    dispatch({ type: "GET_SESSION", payload: sessionData });

    if (sessionData.utcLastUpdate) {
      var now = new Date();
      var utc_timestamp = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds(),
        now.getUTCMilliseconds()
      );
      setIsLiveSession(utc_timestamp / 1000 - sessionData.utcLastUpdate < 300);
      setLastUtcLastUpdate(sessionData.utcLastUpdate);
    }

    // Stats may not be uploaded yet when a session was just created.
    if (!sessionData.stats) {
      console.log("[getLatest] no stats yet for session", sessionId);
      setCounter(0);
      return;
    }
    console.log("[getLatest] appName=", sessionData.appName,
                "stats type=", typeof sessionData.stats,
                "stats[0..80]=", String(sessionData.stats).substring(0, 80));

    try {
      var m = null;
      var appname = null;
      if (sessionData.appName === "DVMate") {
        // Try DVW format first (the text-sniff is unreliable for binary buffers).
        // Fall back to VBStats/PSVB only when DVW parsing produces nothing.
        m = initWithDVWCompressedBuffer(sessionData.stats);
        if (m) {
          appname = "DVMate";
        } else {
          appname = "VBStats";
          m = initWithPSVBCompressedBuffer(sessionData.stats);
        }
      } else {
        appname = sessionData.appName === "VBStats" ? "VBStats" : "DVMate";
        m = appname === "VBStats"
            ? initWithPSVBCompressedBuffer(sessionData.stats)
            : initWithDVWCompressedBuffer(sessionData.stats);
      }
      console.log("[getLatest] appname=", appname, "m=", !!m);
      var mx = null;
      if (appname === "VBStats") {
        const latestData = await getLatestStats(sessionId, 0);
        dispatch({ type: "GET_LATEST", payload: latestData });
        setLatest(latestData);
        mx = parseLatestPSVBStats(latestData, m);
        mx = calculatePSVBStats(mx);
      } else {
        mx = calculateDVWStats(m);
      }
      console.log("[getLatest] mx=", !!mx);
      if (mx) {
        mx.app = appname;
        mx = calculateSideoutStats(mx, appname);
        mx.buffer = unzipBuffer(sessionData.stats);
        mx.videoOnlineUrl = sessionData.videoOnlineUrl ?? null;
        mx.videoStartTimeSeconds = sessionData.videoStartTimeSeconds ?? null;
        mx.videoOffset = sessionData.videoOffset ?? null;
        // `sessionData` always comes from the legacy REST API here (see
        // getSession() in VBLiveAPIAction.js), which has no concept of
        // catapultFocusData -- it's a Firestore-only field. Fetch it
        // directly from Firestore so migrated users don't lose previously
        // saved Catapult Focus data every time they reopen a match this way.
        mx.catapultFocusData = usingFirestore()
          ? await fsFetchCatapultFocusData(sessionId)
          : sessionData.catapultFocusData ?? null;
        // Anchor to the KNOWN existing document id this session was fetched
        // by -- generateMatch/calculateDVWStats may recompute a DIFFERENT
        // guid from the raw buffer (e.g. its deterministic fallback changing
        // over time, or a non-deterministic original save), which would
        // otherwise make a later save create a brand new document instead of
        // updating this one.
        mx.guid = sessionId;
        enrichMatchWithCatapultData(mx, mx.catapultFocusData?.teamSide);
        setMatch(mx);
      }
    } catch (e) {
      console.error("[getLatest] parsing error:", e);
    }
    setCounter(0);
  }, [sessionId, selectedTeam]);

  function getLastState() {
    const lst = localStorage.getItem("lastStateForSession");
    if (lst) {
      return JSON.parse(lst);
    }
    return {};
  };

  function getSessionState(st) {
    if (st) {
      persistLastState(st);
      return st;
    } else {
      return getLastState();
    }
  };

  // Persist the navigation state so the session can be re-opened after a hard
  // reload (when location.state is lost). The raw DVW/PSVB buffers and the
  // compressed session stats can be several MB and blow the localStorage
  // quota, so we tolerate quota failures (dropping the cache) instead of
  // throwing — the current navigation uses location.state directly and is
  // unaffected.
  function persistLastState(st) {
    try {
      localStorage.setItem("lastStateForSession", JSON.stringify(st));
    } catch (e) {
      try {
        localStorage.removeItem("lastStateForSession");
      } catch (_) {
        /* ignore */
      }
      console.warn(
        "lastStateForSession cache skipped (storage quota):",
        e?.message ?? e
      );
    }
  };

  useEffect(() => {
    const indb = msession && msession.id ? true : false;
    if (dvwFileData !== undefined && dvwFileData !== null) {
      // A fresh import (Input.jsx) carries its own winScore/winByTwo "Points
      // to close a DataVolley set" override directly in location.state; a
      // reopened/saved match instead carries it on the fetched `msession`
      // metadata (see storeSession/fsStoreSession). Without this, re-parsing
      // the raw DVW text here would silently fall back to the standard
      // 25/15-point rules and the sets-won match score would come out wrong.
      var m = generateMatch(dvwFileData, {
        winScore: winScore ?? msession?.winScore ?? undefined,
        winByTwo: winByTwo ?? msession?.winByTwo,
      });
      m.filename = filename;
      m.buffer = dvwFileData;
      var mx = calculateDVWStats(m);
      mx.app = "DataVolley";
      if (fileOrigin) mx.origin = fileOrigin;
      mx = calculateSideoutStats(mx, "DataVolley");
      mx.videoOnlineUrl = msession && msession.videoOnlineUrl;
      mx.videoStartTimeSeconds = msession && msession.videoStartTimeSeconds;
      mx.videoOffset = msession && msession.videoOffset;
      // Carry the rule forward so a later Save (the toolbar button below)
      // persists it, keeping future reloads of this same match consistent.
      mx.winScore = winScore ?? msession?.winScore ?? null;
      mx.winByTwo = winByTwo ?? msession?.winByTwo ?? true;
      if (mx.videoOffset && mx.videoOffset !== -1 && mx.videoOnlineUrl !== -1) {
        const firstev = mx.events[0];
        for (var ev of mx.events) {
          ev.VideoPosition = ev.TimeStamp?.getTime() / 1000 - firstev.TimeStamp?.getTime() / 1000 + mx.videoOffset;
        }
      }
      setInDatabase(indb || !!initialInDatabase);
      if (indb && msession?.shareStatus >= 1) {
        const viewerEmail = getAuth().currentUser?.email;
        if (viewerEmail) recordMatchView(msession.id, viewerEmail);
      }
      // Reopening an already-saved match: anchor to its KNOWN existing
      // document id rather than trusting whatever generateMatch recomputed
      // from the raw buffer (which can drift and cause a later save to
      // create a duplicate document instead of updating this one). Only a
      // genuinely new, never-saved-before import falls back to a fresh id.
      if (indb && msession?.id) {
        mx.guid = msession.id;
      } else if (!mx.guid) {
        mx.guid = generateUUID();
      }
      mx.catapultFocusData = msession?.catapultFocusData ?? null;
      enrichMatchWithCatapultData(mx, mx.catapultFocusData?.teamSide);
      const cr = localStorage.getItem("currentReportForMatch");
      if (cr && !location.state?.vssReport) {
        const tokens = cr.split("_");
        const storedReport = parseInt(tokens[0]);
        // 6 (Video Analysis), 9 (Momentum Chart) and 3 (Serve Receives)
        // navigate away to their own page rather than rendering inline —
        // restoring straight to one of them would immediately bounce the
        // user back out of Session.jsx.
        if (storedReport === 6 || storedReport === 9 || storedReport === 3) {
          localStorage.removeItem("currentReportForMatch");
        } else if (mx && mx.TrainingDate.toLocaleString() === tokens[1]) {
          setCurrentReport(storedReport);
        }
      }
      setMatch(mx);
      setAutoRefresh(false);
      setIsLiveSession(false);
      forceUpdate((n) => !n);
    } else if (psvbFileData !== undefined) {
      var m = initWithPSVBCompressedBuffer(psvbFileData);
      m.filename = filename;
      m.buffer = unzipBuffer(psvbFileData);
      var mx = calculatePSVBStats(m);
      mx.app = "VBStats";
      mx = calculateSideoutStats(mx, "VBStats");
      mx.videoOnlineUrl =
        msession && msession.videoOnlineUrl ? msession.videoOnlineUrl : null;
      mx.videoStartTimeSeconds =
        msession && msession.videoStartTimeSeconds
          ? msession.videoStartTimeSeconds
          : -1;
      mx.videoOffset =
        msession && msession.videoOffset ? msession.videoOffset : -1;
      setInDatabase(indb || !!initialInDatabase);
      if (indb && msession?.shareStatus >= 1) {
        const viewerEmail = getAuth().currentUser?.email;
        if (viewerEmail) recordMatchView(msession.id, viewerEmail);
      }
      // Same anchor-to-known-id rule as the DVW branch above.
      if (indb && msession?.id) {
        mx.guid = msession.id;
      } else if (!mx.guid) {
        mx.guid = generateUUID();
      }
      mx.catapultFocusData = msession?.catapultFocusData ?? null;
      enrichMatchWithCatapultData(mx, mx.catapultFocusData?.teamSide);
      setMatch(mx);
      const cr = localStorage.getItem("currentReportForMatch");
      if (cr && !location.state?.vssReport) {
        const tokens = cr.split("_");
        const storedReport = parseInt(tokens[0]);
        // 6 (Video Analysis), 9 (Momentum Chart) and 3 (Serve Receives)
        // navigate away to their own page rather than rendering inline —
        // restoring straight to one of them would immediately bounce the
        // user back out of Session.jsx.
        if (storedReport === 6 || storedReport === 9 || storedReport === 3) {
          localStorage.removeItem("currentReportForMatch");
        } else if (mx && mx.TrainingDate.toLocaleString() === tokens[1]) {
          setCurrentReport(storedReport);
        }
      }
      setAutoRefresh(false);
      setIsLiveSession(false);
      forceUpdate((n) => !n);
    } else {
      // setIsLiveSession(true);
      getLatest();
    }

    const handleScroll = () => {
      const position = window.scrollY;
      setScrollPosition(position);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [getLatest, selectedGame, selectedTeam]);

  useEffect(() => {
    if (isLiveSession) {
      const timerId = schedule();
      return () => clearTimeout(timerId);
    }
  }, [counter, isLiveSession]);

  useEffect(() => {
    if (match && currentReport !== 6 && currentReport !== 9 && currentReport !== 3) {
      localStorage.setItem(
        "currentReportForMatch",
        currentReport + "_" + match.TrainingDate.toLocaleString()
      );
    }
  }, [currentReport]);

  // Video Analysis (6), Momentum Chart (9) and Serve Receives (3) aren't
  // rendered inline — they navigate to their own standalone page. This MUST
  // run in an effect (only when currentReport actually changes), not directly
  // inside renderReport(): renderReport() runs on every render of this
  // component (e.g. live-match Firestore updates), so calling navigate()
  // there would re-trigger the navigation on every single re-render while
  // parked on that report, immediately undoing a "Close" button's
  // navigate(-1) back to this page.
  useEffect(() => {
    if (currentReport === 6) {
      doVideoAnalysis();
    } else if (currentReport === 9) {
      doMomentumChart();
    } else if (currentReport === 3) {
      doServeReceiveReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReport]);

  const schedule = () => {
    const timerId = setTimeout(() => {
      let currCount = countRef.current;
      setCounter((currCount) => currCount + 1);
      // console.log(counter);
      if (counter >= refreshInterval - 1) {
        if (isLiveSession && autoRefresh) {
          getLatest();
        }
        setCounter(0);
      }
    }, 1000);

    return timerId;
  };

  if (loading) {
    return <Spinner />;
  }

  const renderReport = () => {
    if (currentReport === 7) {
      return (
        <MatchCentre
          match={match}
          isLive={isLiveSession}
        />
      );
    } else if (currentReport === 0) {
      return (
        <Summary
          matches={[match]}
          selectedGame={selectedGame}
          selectedTeam={selectedTeam}
        />
      );
    } else if (currentReport === 1) {
      return (
        <BoxScore
          matches={[match]}
          team={selectedTeam === 0 ? match.teamA.Name : match.teamB.Name}
          selectedGame={selectedGame}
          selectedTeam={selectedTeam}
        />
      );
    } else if (currentReport === 2) {
      return (
        <SideoutReport
          matches={[match]}
          team={selectedTeam === 0 ? match.teamA.Name : match.teamB.Name}
          selectedGame={selectedGame}
          selectedTeam={selectedTeam}
        />
      );
      // return <Sideout match={match} selectedGame={selectedGame} selectedTeam={selectedTeam} />
    } else if (currentReport === 3) {
      return null; // navigated away to /servereceivereport by the effect above
    } else if (currentReport === 4) {
      return (
        <AttackZones
          matches={[match]}
          team={selectedTeam === 0 ? match.teamA.Name : match.teamB.Name}
          selectedGame={selectedGame}
          selectedTeam={selectedTeam}
        />
      );
    } else if (currentReport === 5) {
      // const st = {
      //   matches: [match],
      //   team: selectedTeam === 0 ? match.teamA.Name : match.teamB.Name,
      //   selectedGame: selectedGame,
      //   selectedTeam: selectedTeam,
      // };
      // navigate("/hittingchartreport", { state: st });
      return (
        <HittingChartReport
          matches={[match]}
          team={selectedTeam === 0 ? match.teamA.Name : match.teamB.Name}
          selectedGame={selectedGame}
          selectedTeam={selectedTeam}
          verticalScroll={isLiveSession ? scrollPosition - 44 : scrollPosition}
        />
      );
    } else if (currentReport === 6) {
      return null; // navigated away to /videoanalysis by the effect above
    } else if (currentReport === 8) {
      return <PowerPlayReport matches={[match]} />;
    } else if (currentReport === 9) {
      return null; // navigated away to /momentumchart by the effect above
    } else if (currentReport === 10) {
      return <CatapultReport match={match} />;
    } else if (currentReport === 11) {
      return <VSSReports match={match} selectedTeam={selectedTeam} selectedGame={selectedGame} />;
    }
  };

  const doHittingChart = () => {
    const st = {
      matches: [match],
      team: selectedTeam === 0 ? match.teamA.Name : match.teamB.Name,
      selectedGame: selectedGame,
      selectedTeam: selectedTeam,
    };
    navigate("/hittingchartreport", { state: st });
  };

  const doVideoAnalysis = () => {
    const st = {
      matches: [match],
      team: match.teamA.Name,
      selectedGame: selectedGame,
      sessionId,
      isLive: isLiveSession,
    };
    navigate("/videoanalysis", { state: st });
    // return <VideoAnalysis match={match} selectedGame={selectedGame} />;
  };

  const doMomentumChart = () => {
    const st = {
      matches: [match],
      team: match.teamA.Name,
      selectedGame: selectedGame,
      sessionId,
    };
    navigate("/momentumchart", { state: st });
  };

  const doServeReceiveReport = () => {
    const st = {
      matches: [match],
      team: selectedTeam === 0 ? match.teamA.Name : match.teamB.Name,
      selectedGame: selectedGame,
      selectedTeam: selectedTeam,
    };
    navigate("/servereceivereport", { state: st });
  };

  const doUpload = async () => {
    setUploading(true);
    try {
      const ret = await storeSession(match, currentUser);
      if (ret === 0) {
        toast.error("Error uploading session");
      } else {
        localStorage.removeItem("liveMatches");
        toast.success("Session uploaded successfully");
        setInDatabase(true);
      }
    } finally {
      setUploading(false);
    }
  };

  const tabs = [
    { name: "Match Centre", index: 7, current: false },
    { name: "Summary", index: 0, current: false },
    { name: "Box Score", index: 1, current: false },
    { name: "Side-outs Report", index: 2, current: false },
    { name: "Serve Receives", index: 3, current: false },
    { name: "Attack Zones", index: 4, current: false },
    { name: "Hitting Charts", index: 5, current: false },
    { name: "Video Analysis", index: 6, current: false },
    { name: "Momentum Chart", index: 9, current: false },
    { name: "Catapult", index: 10, current: false },
    { name: "VolleyStation Reports", index: 11, current: false },
    ...(match?.hasPowerPlays ? [{ name: "Power Play", index: 8, current: false }] : []),
  ];

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  if (!match) {
    return (
      <div className="flex-col">
        {isLiveSession ? (
          <div className="flex gap-2 mt-2 p-2 h-9 bg-gray-500/20">
            <div className="text-sm font-bold text-white bg-red-700 px-2 h-5">
              Match has not started
            </div>
            {autoRefresh ? (
              <label className="text-sm">
                Auto-refresh in {refreshInterval - counter}s
              </label>
            ) : (
              <label className="text-sm">Auto-refresh is off</label>
            )}

            {autoRefresh ? (
              <div className="">
                <div className="tooltip" data-tip="Turn Auto-refresh Off">
                  <XCircleIcon
                    className="w-5 h-5 ml-2 text-base-content cursor-pointer"
                    onClick={() => setAutoRefresh(false)}
                  />
                </div>
              </div>
            ) : (
              <div className="">
                <div className="tooltip" data-tip="Turn Auto-refresh On">
                  <CheckCircleIcon
                    className="w-5 h-5 ml-2 text-base-content cursor-pointer"
                    onClick={() => setAutoRefresh(true)}
                  />
                </div>
              </div>
            )}
            <div className="tooltip" data-tip="Refresh Now">
              <ArrowPathIcon
                className="w-5 h-5 ml-2 text-base-content cursor-pointer"
                onClick={() => getLatest()}
              />
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    );
  }

  return (
    match && (
      <>
        <div>
          {showingVideo ? (
            <></>
          ) : (
            <div className="flex justify-between">
              <MatchSummary
                matches={[match]}
                team={match.teamA.Name}
                gameSelected={selectedGame}
                onGameSelected={(sgn) => setSelectedGame(sgn)}
                teamSelected={selectedTeam}
                onTeamSelected={(tmn) => setSelectedTeam(tmn)}
                onSaveToDatabase={() => doUpload()}
                inDatabase={inDatabase}
                uploading={uploading}
                isLive={isLiveSession}
              ></MatchSummary>
            </div>
          )}
          <div>
            <div className="grid grid-cols-1 sm:hidden">
              <div className="grid grid-cols-6">
                <div className="col-span-1">
                  <label className="text-sm pt-1">{t("Report")}</label>
                </div>
                <div className="col-span-5">
                  <div className="flex gap-1">
                    <select
                      onChange={(e) =>
                        setCurrentReport(
                          tabs.find((tab) => tab.name === e.target.value).index
                        )
                      }
                      value={
                        tabs.find((tab) => tab.index === currentReport).name
                      }
                      aria-label="Select a tab"
                      className="select-generic text-sm"
                    >
                      {tabs.map((tab) => (
                        <option key={tab.name} value={tab.name}>
                          {t(tab.name)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden sm:block">
              <div className="border-b border-gray-200">
                <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                  {tabs.map((tab) => (
                    <a
                      key={tab.name}
                      onClick={() => setCurrentReport(tab.index)}
                      aria-current={
                        tab.index === currentReport ? "page" : undefined
                      }
                      className={classNames(
                        tab.index === currentReport
                          ? "border-primary/80 text-primary/80"
                          : "border-transparent text-base-content hover:border-base-content/30 hover:text-base-content/70",
                        "whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium"
                      )}
                    >
                      {t(tab.name)}
                    </a>
                  ))}
                </nav>
              </div>
            </div>
          </div>
          <div className="flex-col">
            {isLiveSession ? (
              <div className="flex gap-2 mt-2 p-2 h-9 bg-gray-500/20">
                <div className="text-sm font-bold text-white bg-red-700 px-2 h-5">
                  LIVE
                </div>
                {autoRefresh ? (
                  <label className="text-sm">
                    Auto-refresh in {refreshInterval - counter}s
                  </label>
                ) : (
                  <label className="text-sm">Auto-refresh is off</label>
                )}

                {autoRefresh ? (
                  <div className="">
                    <div className="tooltip" data-tip="Turn Auto-refresh Off">
                      <XCircleIcon
                        className="w-5 h-5 ml-2 text-base-content cursor-pointer"
                        onClick={() => setAutoRefresh(false)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="">
                    <div className="tooltip" data-tip="Turn Auto-refresh On">
                      <CheckCircleIcon
                        className="w-5 h-5 ml-2 text-base-content cursor-pointer"
                        onClick={() => setAutoRefresh(true)}
                      />
                    </div>
                  </div>
                )}
                <div className="tooltip" data-tip="Refresh Now">
                  <ArrowPathIcon
                    className="w-5 h-5 ml-2 text-base-content cursor-pointer"
                    onClick={() => getLatest()}
                  />
                </div>
              </div>
            ) : (
              <></>
            )}
            <div className="mt-4">{renderReport()}</div>
          </div>
        </div>
      </>
    )
  );
}

export default Session;
